import type { Visual } from '@/types/visual';
// Centralized mock data for learning components

export interface Note {
  id: string;
  content: string;
  timestamp: Date;
  type: 'ai' | 'personal';
  starred?: boolean;
}

export interface Highlight {
  id: string;
  content: string;
  type: 'summary' | 'key-point';
}

export interface PersonalNotesDocument {
  content: string;
  lastSaved: Date | null;
  lessonId: string;
}

export interface Question {
  id: string;
  type: 'multiple-choice' | 'multiple-select' | 'true-false' | 'fill-blank';
  question: string;
  options?: string[];
  correctAnswer: string | number | number[]; // number[] for multiple-select
  explanation?: string;
  placeholder?: string; // for fill-blank
  caseSensitive?: boolean; // for fill-blank
  acceptedAnswers?: string[]; // for fill-blank (synonyms/variations)
  /** A figure the question is about (test view: numbered, an unseen variant). */
  visual?: Visual;
  /** The concept's diagram, shown with the explanation once answered. */
  explanationVisual?: Visual;
}

export interface Flashcard {
  id: string;
  front: string;
  back: string;
  difficulty: 'easy' | 'medium' | 'hard';
  mastered: boolean;
  /** Per-lesson concept id (for FSRS grading); optional in mock data. */
  concept_id?: string | null;
  /** A figure on the front (recall view: one label hidden) or the back. */
  frontVisual?: Visual;
  backVisual?: Visual;
  /** Made from the concept's picture; not part of the session signature. */
  fromVisual?: boolean;
}

export interface Source {
  id: string;
  name: string;
  type: 'pdf' | 'link' | 'video' | 'text';
  url?: string;
  size?: string;
  uploadedAt: Date;
  viewed?: boolean;
  /**
   * Populated when this Source represents a normalized API source.
   * Drives the layered viewer dispatch (extension first, mime fallback)
   * so user-uploaded `.docx` / `.md` / `.csv` etc. land in the right
   * renderer instead of being lumped under `type='text'`.
   */
  mime_type?: string;
  original_filename?: string;
}

export interface GlossaryTerm {
  id: string;
  term: string;
  definition: string;
  category: string;
  examples?: string[];
}

export interface Reference {
  id: string;
  title: string;
  description: string;
  url: string;
  type: 'article' | 'documentation' | 'tutorial' | 'research';
}

export interface Version {
  id: string;
  label: string;
  changes: string;
  createdAt: Date;
  createdBy: string;
  isCurrent: boolean;
}

export interface TranscriptItem {
  id: string;
  startTime: number;
  endTime?: number;
  timestamp: string;
  text: string;
  /** Which podcast voice speaks the line. Absent in older fixtures, which then
   *  render as one unattributed block. */
  speaker?: 'host_a' | 'host_b';
}

// Mock Highlights Data
export const mockHighlights: Highlight[] = [
  {
    id: '1',
    content:
      'Machine learning is a subset of artificial intelligence that enables computers to learn from experience without being explicitly programmed. It focuses on developing algorithms and statistical models that improve performance on specific tasks through data.',
    type: 'summary',
  },
  {
    id: '2',
    content:
      'Supervised learning uses labeled training data where the correct answers are known. The algorithm learns patterns from these examples and applies them to make predictions on new, unseen data. Common applications include classification (categorizing data) and regression (predicting numerical values).',
    type: 'key-point',
  },
  {
    id: '3',
    content:
      'The machine learning workflow involves three critical steps: (1) Data Preparation - collecting and cleaning quality data, then splitting it into training (60-70%), validation (15-20%), and test sets (15-20%); (2) Model Training - selecting algorithms and tuning parameters; (3) Evaluation - measuring performance and ensuring the model generalizes well to new data.',
    type: 'key-point',
  },
  {
    id: '4',
    content:
      'Overfitting and underfitting are two major challenges in machine learning. Overfitting occurs when a model is too complex and memorizes training data rather than learning general patterns, leading to poor performance on new data. Underfitting happens when a model is too simple to capture underlying patterns. The goal is finding the right balance for good generalization.',
    type: 'key-point',
  },
  {
    id: '5',
    content:
      'Machine learning powers numerous real-world applications across industries: recommendation systems (Netflix, Amazon), email spam filtering, medical diagnosis, autonomous vehicles, fraud detection, natural language processing, and computer vision. Its ability to scale and discover patterns automatically makes it invaluable for solving complex problems.',
    type: 'key-point',
  },
];

// Mock Personal Notes Document
export const mockPersonalNotesDocument: PersonalNotesDocument = {
  content: '',
  lastSaved: null,
  lessonId: 'lesson-1',
};

// Mock Notes Data (kept for backward compatibility)
export const mockNotes: Note[] = [];

// Mock Questions Data
export const mockQuestions: Question[] = [
  {
    id: '1',
    type: 'multiple-choice',
    question: 'What is the main goal of supervised learning?',
    options: [
      'To find hidden patterns in unlabeled data',
      'To learn from labeled examples and make predictions on new data',
      'To optimize system performance without any data',
      'To create unsupervised clusters',
    ],
    correctAnswer: 1,
    explanation:
      'Supervised learning uses labeled training data to learn patterns and make predictions on new, unseen data.',
  },
  {
    id: '2',
    type: 'multiple-select',
    question:
      'Which of the following are types of machine learning? (Select all that apply)',
    options: [
      'Supervised learning',
      'Quantum learning',
      'Unsupervised learning',
      'Reinforcement learning',
    ],
    correctAnswer: [0, 2, 3],
    explanation:
      'The three main types of machine learning are supervised learning, unsupervised learning, and reinforcement learning. Quantum learning is not a standard ML type.',
  },
  {
    id: '3',
    type: 'true-false',
    question:
      'Overfitting occurs when a model performs well on training data but poorly on test data.',
    correctAnswer: 'true',
    explanation:
      'Overfitting happens when a model memorizes the training data too closely and fails to generalize to new examples.',
  },
  {
    id: '4',
    type: 'fill-blank',
    question:
      'In machine learning, the process of finding patterns in data without labeled examples is called _____ learning.',
    correctAnswer: 'unsupervised',
    acceptedAnswers: ['unsupervised', 'Unsupervised'],
    placeholder: 'Type your answer...',
    caseSensitive: false,
    explanation:
      'Unsupervised learning finds patterns and structures in data without labeled examples or guidance.',
  },
  {
    id: '5',
    type: 'multiple-choice',
    question:
      'Which of the following is NOT a common way to split data in machine learning?',
    options: ['Training set', 'Validation set', 'Test set', 'Production set'],
    correctAnswer: 3,
    explanation:
      'The standard splits are training, validation, and test sets. "Production set" is not a standard data split.',
  },
  {
    id: '6',
    type: 'true-false',
    question:
      'Machine learning algorithms can learn without being explicitly programmed.',
    correctAnswer: 'true',
    explanation:
      'This is the fundamental concept of machine learning - algorithms improve performance through experience.',
  },
  {
    id: '7',
    type: 'fill-blank',
    question:
      'The phenomenon where a machine learning model performs poorly on both training and test data is called _____.',
    correctAnswer: 'underfitting',
    acceptedAnswers: ['underfitting', 'Underfitting', 'under-fitting'],
    placeholder: 'Type your answer...',
    caseSensitive: false,
    explanation:
      'Underfitting occurs when a model is too simple to capture the underlying patterns in the data.',
  },
  {
    id: '8',
    type: 'multiple-select',
    question:
      'Which of these are common applications of machine learning? (Select all that apply)',
    options: [
      'Email spam filtering',
      'Manual data entry',
      'Image recognition',
      'Recommendation systems',
    ],
    correctAnswer: [0, 2, 3],
    explanation:
      'Email spam filtering, image recognition, and recommendation systems are all common ML applications. Manual data entry is not an ML application.',
  },
];

// Mock Flashcards Data
export const mockFlashcards: Flashcard[] = [
  {
    id: '1',
    front:
      'What is the difference between supervised and unsupervised learning?',
    back: 'Supervised learning uses labeled data with known correct answers, while unsupervised learning finds patterns in unlabeled data.',
    difficulty: 'medium',
    mastered: false,
  },
  {
    id: '2',
    front: 'Name three types of machine learning',
    back: 'Supervised learning, unsupervised learning, and reinforcement learning.',
    difficulty: 'easy',
    mastered: true,
  },
  {
    id: '3',
    front: 'What is overfitting in machine learning?',
    back: 'Overfitting occurs when a model learns the training data too well and performs poorly on new, unseen data.',
    difficulty: 'hard',
    mastered: false,
  },
];

// Mock Sources Data
export const mockSources: Source[] = [
  {
    id: '1',
    name: 'Chapter 1 - Introduction.pdf',
    type: 'pdf',
    size: '2.4 MB',
    uploadedAt: new Date('2024-01-15'),
    viewed: true,
  },
  {
    id: '2',
    name: 'Wikipedia - Machine Learning',
    type: 'link',
    url: 'https://wikipedia.org/ml',
    uploadedAt: new Date('2024-01-14'),
    viewed: false,
  },
  {
    id: '3',
    name: 'ML Basics Video',
    type: 'video',
    size: '45 min',
    uploadedAt: new Date('2024-01-13'),
    viewed: true,
  },
];

// Mock Glossary Terms
export const mockGlossaryTerms: GlossaryTerm[] = [
  {
    id: '1',
    term: 'Machine Learning',
    definition:
      'A subset of artificial intelligence that enables computers to learn and improve from experience without being explicitly programmed.',
    category: 'Core Concepts',
    examples: ['Spam detection', 'Image recognition', 'Recommendation systems'],
  },
  {
    id: '2',
    term: 'Supervised Learning',
    definition:
      'A type of machine learning where algorithms learn from labeled training data.',
    category: 'Learning Types',
    examples: ['Classification', 'Regression'],
  },
  {
    id: '3',
    term: 'Unsupervised Learning',
    definition:
      'Machine learning technique that finds patterns in data without labeled examples.',
    category: 'Learning Types',
    examples: ['Clustering', 'Dimensionality reduction'],
  },
  {
    id: '4',
    term: 'Neural Network',
    definition:
      'A computing system inspired by biological neural networks that constitute the basis of deep learning.',
    category: 'Deep Learning',
    examples: ['Convolutional Neural Networks', 'Recurrent Neural Networks'],
  },
];

// Mock References Data
export const mockReferences: Reference[] = [
  {
    id: '1',
    title: 'Introduction to Machine Learning',
    description:
      'Comprehensive guide covering fundamental concepts and applications',
    url: 'https://example.com/ml-intro',
    type: 'article',
  },
  {
    id: '2',
    title: 'Scikit-learn Documentation',
    description: 'Official documentation for the popular Python ML library',
    url: 'https://scikit-learn.org/stable/',
    type: 'documentation',
  },
  {
    id: '3',
    title: 'Deep Learning Tutorial',
    description: 'Step-by-step guide to building neural networks',
    url: 'https://example.com/deep-learning',
    type: 'tutorial',
  },
];

// Mock Lesson Data
export interface LessonData {
  id: string;
  title: string;
  estimatedReadTime: number;
  timeSpent: number;
  textContentViewed: boolean;
  videoProgress: number;
  audioProgress: number;
  mindmapViewed: boolean;
  notesTaken: number;
  personalNotesCount: number;
  flashcardsCompleted: number;
  quizScore: number;
  quizAttempts: number;
  sourcesViewed: number;
  glossaryViewed: boolean;
  currentLessonStyle: string;
  totalSources: number;
  lastAccessed: Date;
  currentStyle: 'standard' | 'eli5' | 'story' | 'revision';
  hasNewContent: boolean;
}

export const mockLessonData: LessonData = {
  id: 'lesson-1',
  title: 'Introduction to Machine Learning',
  estimatedReadTime: 15,
  timeSpent: 8,
  textContentViewed: false,
  videoProgress: 0,
  audioProgress: 0,
  mindmapViewed: false,
  notesTaken: 2,
  personalNotesCount: 1,
  flashcardsCompleted: 0,
  quizScore: 0,
  quizAttempts: 0,
  sourcesViewed: 1,
  glossaryViewed: false,
  currentLessonStyle: 'standard',
  totalSources: 3,
  lastAccessed: new Date(),
  currentStyle: 'standard',
  hasNewContent: false,
};

// Mock Versions Data
export const mockVersions: Version[] = [
  {
    id: 'v1.3',
    label: 'v1.3 - Enhanced',
    changes: 'Added advanced examples and interactive elements',
    createdAt: new Date('2024-01-20'),
    createdBy: 'AI Instructor',
    isCurrent: true,
  },
  {
    id: 'v1.2',
    label: 'v1.2 - Improved',
    changes: 'Updated content structure and added visual aids',
    createdAt: new Date('2024-01-15'),
    createdBy: 'Content Team',
    isCurrent: false,
  },
  {
    id: 'v1.1',
    label: 'v1.1 - Revised',
    changes: 'Fixed typos and improved clarity',
    createdAt: new Date('2024-01-10'),
    createdBy: 'Editor',
    isCurrent: false,
  },
  {
    id: 'v1.0',
    label: 'v1.0 - Initial',
    changes: 'Original lesson content',
    createdAt: new Date('2024-01-05'),
    createdBy: 'Content Creator',
    isCurrent: false,
  },
];
